import { useState, useEffect, useMemo } from "react";
import { Route, ChevronRight, TrendingUp, Trophy, Loader2, ArrowRightLeft, BarChart3 } from "lucide-react";
import { PostLoadCard, PostLoadCardHeader } from "@/components/post-load/PostLoadCard";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/* ─── Types ─── */

interface LanePerf {
  lane: string; // e.g. "TX → GA"
  originState: string;
  destState: string;
  volume: number;
  avgRpm: number;
  avgDaysToBook: number;
}

/* ─── Sample data for empty / demo state ─── */

const SAMPLE_LANES: LanePerf[] = [
  { lane: "TX → GA", originState: "TX", destState: "GA", volume: 12, avgRpm: 2.48, avgDaysToBook: 1.2 },
  { lane: "CA → AZ", originState: "CA", destState: "AZ", volume: 9, avgRpm: 2.31, avgDaysToBook: 0.8 },
  { lane: "IL → OH", originState: "IL", destState: "OH", volume: 7, avgRpm: 2.15, avgDaysToBook: 1.5 },
];

/* ─── Component ─── */

export default function AILaneOptimizerCard() {
  const { user } = useAuth();
  const [lanes, setLanes] = useState<LanePerf[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    if (!user) {
      setLanes(SAMPLE_LANES);
      setIsDemo(true);
      setLoading(false);
      return;
    }
    fetchLanePerformance(user.id);
  }, [user]);

  async function fetchLanePerformance(userId: string) {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("loads")
        .select("origin_state, destination_state, rate_amount, total_miles, posted_at, booked_at")
        .eq("created_by", userId)
        .in("status", ["booked", "completed"] as any)
        .gt("rate_amount", 0)
        .gt("total_miles", 0)
        .not("booked_at", "is", null);

      if (error || !data?.length) {
        setLanes(SAMPLE_LANES);
        setIsDemo(true);
        setLoading(false);
        return;
      }

      // Group by origin_state → destination_state
      const groups = new Map<string, { rpms: number[]; daysToBook: number[] }>();
      for (const l of data) {
        const key = `${l.origin_state}|${l.destination_state}`;
        if (!groups.has(key)) groups.set(key, { rpms: [], daysToBook: [] });
        const g = groups.get(key)!;
        g.rpms.push(Number(l.rate_amount) / Number(l.total_miles));
        if (l.posted_at && l.booked_at) {
          const days = (new Date(l.booked_at).getTime() - new Date(l.posted_at).getTime()) / 86_400_000;
          if (days >= 0) g.daysToBook.push(days);
        }
      }

      const result: LanePerf[] = [];
      groups.forEach((g, key) => {
        const [oState, dState] = key.split("|");
        const avgRpm = g.rpms.reduce((s, v) => s + v, 0) / g.rpms.length;
        const avgDays = g.daysToBook.length
          ? g.daysToBook.reduce((s, v) => s + v, 0) / g.daysToBook.length
          : 0;
        result.push({
          lane: `${oState} → ${dState}`,
          originState: oState,
          destState: dState,
          volume: g.rpms.length,
          avgRpm: Math.round(avgRpm * 100) / 100,
          avgDaysToBook: Math.round(avgDays * 10) / 10,
        });
      });

      // Sort by volume desc, then RPM desc
      result.sort((a, b) => b.volume - a.volume || b.avgRpm - a.avgRpm);

      if (result.length === 0) {
        setLanes(SAMPLE_LANES);
        setIsDemo(true);
      } else {
        setLanes(result);
        setIsDemo(false);
      }
    } catch {
      setLanes(SAMPLE_LANES);
      setIsDemo(true);
    }
    setLoading(false);
  }

  const topLanes = lanes.slice(0, 3);
  const totalVolume = lanes.reduce((s, l) => s + l.volume, 0);
  const avgRpmAll = lanes.length
    ? Math.round((lanes.reduce((s, l) => s + l.avgRpm, 0) / lanes.length) * 100) / 100
    : 0;
  const fastestBook = lanes.length
    ? Math.min(...lanes.map((l) => l.avgDaysToBook))
    : 0;

  const bannerPositive = avgRpmAll >= 2.0;

  return (
    <PostLoadCard className="flex flex-col relative">
      <PostLoadCardHeader
        icon={<Route className="h-3.5 w-3.5" style={{ color: "hsl(170 50% 15%)" }} />}
        title="ATLAS Lane Optimizer"
        demoHintKey="section.laneOptimizer"
        variant="atlas"
      />


      <div className="px-3 pb-3 pt-3 flex flex-col flex-1 space-y-2.5">
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Summary banner */}
            <div
              className="rounded-md px-3 py-2 flex items-center gap-2"
              style={{
                backgroundColor: bannerPositive
                  ? "hsl(var(--brand-teal-deep) / 0.10)"
                  : "hsl(35 95% 50% / 0.10)",
              }}
            >
              <Trophy
                className="h-3 w-3 shrink-0"
                style={{
                  color: bannerPositive ? "hsl(var(--brand-teal-deep))" : "hsl(35 95% 50%)",
                }}
              />
              <span
                className="text-[11px] font-semibold"
                style={{
                  color: bannerPositive ? "hsl(var(--brand-teal-deep))" : "hsl(35 80% 42%)",
                }}
              >
                {isDemo
                  ? "See your top lanes after booking loads"
                  : `${lanes.length} lane${lanes.length !== 1 ? "s" : ""} ranked by performance`}
              </span>
            </div>

            {/* Description */}
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              {isDemo
                ? "Book loads to see your highest-performing corridors ranked by volume, RPM, and speed-to-book."
                : "Your corridors ranked by volume & RPM. Double down on winners."}
            </p>

            {/* Stats grid */}
            <div className="grid grid-cols-3 gap-1.5">
              <div className="rounded-md px-2 py-1.5 bg-muted/50">
                <div className="flex items-center gap-1 mb-0.5">
                  <BarChart3 className="h-2.5 w-2.5 text-muted-foreground" />
                  <span className="text-[9px] text-muted-foreground uppercase tracking-wide">Volume</span>
                </div>
                <span className="text-xs font-semibold text-foreground">{totalVolume}</span>
              </div>
              <div className="rounded-md px-2 py-1.5 bg-muted/50">
                <div className="flex items-center gap-1 mb-0.5">
                  <TrendingUp className="h-2.5 w-2.5 text-muted-foreground" />
                  <span className="text-[9px] text-muted-foreground uppercase tracking-wide">Avg RPM</span>
                </div>
                <span className="text-xs font-semibold text-foreground">${avgRpmAll.toFixed(2)}</span>
              </div>
              <div className="rounded-md px-2 py-1.5 bg-muted/50">
                <div className="flex items-center gap-1 mb-0.5">
                  <Route className="h-2.5 w-2.5 text-muted-foreground" />
                  <span className="text-[9px] text-muted-foreground uppercase tracking-wide">Fastest</span>
                </div>
                <span className="text-xs font-semibold text-foreground">{fastestBook}d</span>
              </div>
            </div>

            {/* Leaderboard */}
            <div className="space-y-1.5">
              <span className="text-[9px] text-muted-foreground uppercase tracking-wide font-medium">
                Top lanes
              </span>
              {topLanes.map((l, i) => (
                <div key={l.lane} className="flex items-center justify-between text-[11px]">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span
                      className="text-[9px] font-bold w-4 text-center shrink-0"
                      style={{ color: i === 0 ? "hsl(var(--brand-teal-deep))" : undefined }}
                    >
                      #{i + 1}
                    </span>
                    <ArrowRightLeft className="h-2.5 w-2.5 text-muted-foreground shrink-0" />
                    <span className="text-foreground font-medium truncate">{l.lane}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] font-semibold" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                      ${l.avgRpm.toFixed(2)}
                    </span>
                    <Badge
                      variant="secondary"
                      className="text-[9px] px-1.5 py-0 bg-muted text-muted-foreground border-0"
                    >
                      {l.volume} loads
                    </Badge>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-auto pt-1">
              <button
                className="flex items-center gap-1 text-[11px] font-semibold w-full rounded-md px-3 py-2 transition-colors"
                style={{
                  backgroundColor: "hsl(170 85% 32% / 0.12)",
                  color: "hsl(var(--brand-teal-deep))",
                }}
              >
                {isDemo ? "Post a Load to Start" : "View All Lanes"}
                <ChevronRight className="h-3 w-3 ml-auto" />
              </button>
            </div>
          </>
        )}
      </div>
    </PostLoadCard>
  );
}
